"use client";

import { motion } from "framer-motion";
import { useTranslations } from "next-intl";

interface HamburgerButtonProps {
  isOpen: boolean;
  onToggle: () => void;
  ariaLabel?: string;
}

export function HamburgerButton({
  isOpen,
  onToggle,
  ariaLabel,
}: HamburgerButtonProps) {
  const t = useTranslations("Navigation");

  return (
    <button
      type="button"
      onClick={onToggle}
      aria-label={ariaLabel ?? (isOpen ? t("close_menu") : t("open_menu"))}
      aria-expanded={isOpen}
      className="2xl:hidden p-2 text-(--text-primary) cursor-pointer flex items-center gap-2 group"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        width="24"
        height="24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="w-6 h-6 group-hover:text-(--brand-primary) transition-colors"
      >
        {/* Top line */}
        <motion.path
          d="M4 5h16"
          animate={
            isOpen
              ? { rotate: 45, y: 7, originX: 0.5, originY: 0.5 }
              : { rotate: 0, y: 0, originX: 0.5, originY: 0.5 }
          }
          transition={{ type: "spring", stiffness: 400, damping: 30 }}
        />
        {/* Middle line */}
        <motion.path
          d="M4 12h16"
          animate={isOpen ? { opacity: 0 } : { opacity: 1 }}
          transition={{ duration: 0.15 }}
        />

        {/* Bottom line */}
        <motion.path
          d="M4 19h16"
          animate={
            isOpen
              ? { rotate: -45, y: -7, originX: 0.5, originY: 0.5 }
              : { rotate: 0, y: 0, originX: 0.5, originY: 0.5 }
          }
          transition={{ type: "spring", stiffness: 400, damping: 30 }}
        />
      </svg>
      <span className="hidden md:block text-xs font-bold uppercase tracking-widest text-(--text-primary) group-hover:text-(--brand-primary) transition-colors">
        {isOpen ? t("close") : t("menu")}
      </span>
    </button>
  );
}
